// Closed-brief outcome model for the supplier portal (Closed-brief outcomes
// brief §1). DERIVED, never stored — computed from the brief status, whether an
// award exists (and to whom), and the supplier's own response record. Storing a
// column would let it drift out of sync with the brief and the response.
//
// Pure JS — no React, no Supabase — so it's shared by the portal page, the
// dashboard surfacing, and the admin "abandoned drafts" view, and unit-tested.

export const CLOSED_OUTCOME = {
  WON:          'won',          // this supplier was awarded (existing award view owns the UI)
  NOT_SELECTED: 'not_selected', // submitted a quote, awarded to someone else
  NO_AWARD:     'no_award',     // submitted a quote, brief closed with no award
  DECLINED:     'declined',     // supplier declined the brief
  DRAFT_UNSENT: 'draft_unsent', // started a draft, never submitted
  NO_RESPONSE:  'no_response',  // did nothing
};

// Resolve in priority order (brief §1): a submitted quote beats a draft, a
// draft beats nothing; a decline is explicit. Returns null for a brief that
// isn't closed (it has no outcome yet).
//
//   brief:     { status, awarded_supplier_id }
//   recipient: { supplier_id, status }  status ∈ pending|accepted|responded|declined|withdrawn
//   response:  { is_submitted, last_autosaved_at, updated_at } | null
export function deriveClosedOutcome({ brief, recipient, response, supplierId } = {}) {
  if (!brief || brief.status !== 'closed') return null;

  const sid = supplierId ?? recipient?.supplier_id ?? null;
  const awarded = !!brief.awarded_supplier_id;

  if (awarded && sid && brief.awarded_supplier_id === sid) return CLOSED_OUTCOME.WON;
  if (recipient?.status === 'declined') return CLOSED_OUTCOME.DECLINED;

  const submitted = !!response?.is_submitted || recipient?.status === 'responded';
  if (submitted) return awarded ? CLOSED_OUTCOME.NOT_SELECTED : CLOSED_OUTCOME.NO_AWARD;

  // State 4 — a draft was started (autosaved at least once) but never sent.
  const draftStarted = !!response && !response.is_submitted
    && !!(response.last_autosaved_at || response.updated_at);
  if (draftStarted) return CLOSED_OUTCOME.DRAFT_UNSENT;

  return CLOSED_OUTCOME.NO_RESPONSE;
}

// Per-state copy (brief §3). `reasonBox` = show the "From Fusion 2K" reason box
// (§4); `notice`/`whatsNew` = dashboard surfacing (§5). Copy rules baked in:
// never thank a non-responder; plain "Closed" for non-quoters; no hollow
// future-volume promises.
export const CLOSED_OUTCOME_COPY = {
  not_selected: {
    eyebrow: 'Closed',
    heading: 'This one went to another supplier',
    body: 'Thanks for quoting on this brief. It’s been awarded to another supplier this time.',
    reasonBox: true,
    notice: true,
    whatsNew: true,
  },
  no_award: {
    eyebrow: 'Closed',
    heading: 'This one isn’t going ahead',
    // Must not imply they lost to a competitor — the work evaporated.
    body: 'This brief has been closed without going ahead. Thanks for the time you spent quoting.',
    reasonBox: true,
    notice: true,
    whatsNew: true,
  },
  no_response: {
    eyebrow: 'Closed',
    heading: 'This brief has closed',
    // Neutral. No thanks, no reason box.
    body: 'This brief is now closed. Here’s what was asked for.',
    reasonBox: false,
    notice: false,
    whatsNew: false,
  },
  draft_unsent: {
    eyebrow: 'Closed',
    heading: 'This closed before you sent your quote',
    body: 'This brief closed while your quote was still a draft — it was never submitted.',
    reasonBox: false,
    notice: false,
    whatsNew: true,
  },
  declined: {
    eyebrow: 'Closed',
    heading: 'You declined this brief',
    body: 'You declined this brief.',
    reasonBox: false,
    notice: false,
    whatsNew: false,
  },
};
